import { ColumnHelper } from '@tanstack/react-table';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { ReportMetricsResponse } from 'src/contexts/ReportMetricsContext/ReportMetricsContextV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useReportV2ColumnPrecisionByColumnId } from 'src/hooks/useColumnUserSetting';
import {
  FormatOption,
  FormatOption_Percent,
} from 'src/modals/EditTableColumns';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { ReportColumnPersonalizationFlags } from 'src/utils/columns/columnUtils.types';
import { ListingReportTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import {
  getCustomColumnDisplayName,
  isCustomColumnName,
  isWindowFunction,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { YES_NO_ENUM_FILTER_TO_CID } from 'src/utils/constants/contentIdMaps';
import { ToYesNoEnum } from 'src/utils/eventQueryUtils';
import {
  applyNumberFormatting,
  formatCurrency,
} from 'src/utils/numberFormatter';
import {
  getTagDisplayNameFromReportingColumnName,
  isTagReportingColumnName,
  ReportTypesV2,
} from 'src/utils/reportsUtils';
import {
  getReportValueTypeDisplayConfig,
  sortMixedSingleAndArray,
  sortMoney,
  sortNullableBoolean,
} from 'src/utils/tableUtils';
import { ReportValueType, UiMoney } from 'src/WebApiController';

import { DateCell } from '../Table/TableCells';
import { SHIMMERING_DIV_HEIGHT_REPORT } from './ReportTableCommon.constants';
import { reportV2AccessorFn } from './ReportTableCommon.utils';
import { TextCell } from './TextCell';

export const defaultColumnHelperAccessor = (
  columnHelper: ColumnHelper<ReportMetricsResponse | null>,
  columnId: string,
  columnValue: string,
  headerContentId: ContentId,
  reportType: ReportTypesV2,
  valueType: ReportValueType | undefined,
  personalizationFlags: Partial<ReportColumnPersonalizationFlags> | undefined,
  hideFooter?: boolean | undefined
) => {
  const serverSideDisplayConfig = getReportValueTypeDisplayConfig(valueType);

  // Personalization flags override server side display config
  const isNumeric =
    personalizationFlags?.isNumeric ?? serverSideDisplayConfig.isNumeric;
  const isCurrency =
    personalizationFlags?.isCurrency ?? serverSideDisplayConfig.isCurrency;
  let isPercent =
    personalizationFlags?.isPercent ?? serverSideDisplayConfig.isPercent;
  const isDateTime =
    personalizationFlags?.isDateTime ?? serverSideDisplayConfig.isDateTime;
  const isBoolean = serverSideDisplayConfig.isBoolean;
  const isArray = serverSideDisplayConfig.isArray;
  const isInteger =
    personalizationFlags?.isInteger ?? serverSideDisplayConfig.isInteger;

  const align = isNumeric ? 'right' : 'left';
  const isCustomColumn = isCustomColumnName(columnId);
  if (isCustomColumn && isWindowFunction(columnValue)) {
    isPercent = true;
  }
  const isTagColumn = isTagReportingColumnName(columnId);

  return columnHelper.accessor(reportV2AccessorFn(columnId), {
    id: columnId,
    header: () => (
      <TableHeaderCellDiv align={align}>
        {isCustomColumn ? (
          <span>{getCustomColumnDisplayName(columnId)}</span>
        ) : isTagColumn ? (
          <span>{getTagDisplayNameFromReportingColumnName(columnId)}</span>
        ) : (
          <Content id={headerContentId} />
        )}
      </TableHeaderCellDiv>
    ),
    size: COLUMN_DEFAULT_SIZE_BASE,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell(params) {
      const { userDefinedPrecision } = useReportV2ColumnPrecisionByColumnId(
        columnId,
        reportType
      );

      const value = params.getValue<string | null>();
      const valueAsArray = params.getValue<string[] | null>();
      const valueAsMoney = params.getValue<UiMoney>();

      if (!params.row.original) {
        return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
      }

      // Handle columns with personalization
      if (isCurrency && valueAsMoney.currency) {
        return (
          <TableCellDiv align={align}>
            {userDefinedPrecision
              ? applyNumberFormatting({
                  precision: userDefinedPrecision,
                  inputNumber: valueAsMoney.amt,
                  currencyCode: valueAsMoney.currency,
                  currencyDecimalDigits: valueAsMoney.dec,
                })
              : (formatCurrency(valueAsMoney.amt, valueAsMoney.currency) ?? (
                  <Content id={ContentId.NA} />
                ))}
          </TableCellDiv>
        );
      }

      let isPercentInner = isPercent;
      const isPercentOption =
        userDefinedPrecision &&
        Object.values(FormatOption_Percent).includes(
          userDefinedPrecision as FormatOption_Percent
        );
      if (isCustomColumn && isPercentOption) {
        isPercentInner = true;
      }

      if (isNumeric) {
        return (
          <TableCellDiv align={align}>
            {value != null && !isNaN(Number(value))
              ? applyNumberFormatting({
                  inputNumber: value,
                  isPercent: isPercentInner,
                  precision: isInteger
                    ? (userDefinedPrecision ??
                      FormatOption.Percent_RoundToWhole)
                    : userDefinedPrecision,
                })
              : (value ?? <Content id={ContentId.NA} />)}
          </TableCellDiv>
        );
      }

      if (isDateTime) {
        const dateValue = isArray
          ? valueAsArray
          : value
            ? new Date(value).toISOString()
            : value;
        return (
          <DateCell
            precision={
              isArray
                ? (userDefinedPrecision ?? FormatOption.DateTime_MMDDYY)
                : userDefinedPrecision
            }
            date={dateValue}
            align={align}
            hideRelativeTerms
            useSiteTimeZone={columnId !== ListingReportTableColumnId.EventDate}
          />
        );
      }

      if (isBoolean) {
        const yesNoEnum = ToYesNoEnum(value as boolean | null);

        return (
          <TableCellDiv align={'left'}>
            <Content
              id={
                yesNoEnum != null
                  ? YES_NO_ENUM_FILTER_TO_CID[yesNoEnum]
                  : ContentId.NA
              }
            />
          </TableCellDiv>
        );
      }

      return (
        <TextCell
          {...params}
          value={isArray ? (valueAsArray || []).join(', ') : value}
          align={align}
        />
      );
    },
    footer: function Footer(params) {
      const { userDefinedPrecision } = useReportV2ColumnPrecisionByColumnId(
        columnId,
        reportType
      );

      const original = params.table.options.meta?.summaryData;

      if (!original) {
        return <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_REPORT} />;
      }
      const value = params.column.accessorFn?.(original, -1);

      if (isArray || hideFooter) {
        return null;
      }

      if (isCurrency) {
        const valueAsMoney = value as UiMoney | null;
        return (
          <TableCellDiv align={align}>
            {valueAsMoney ? (
              <>
                {userDefinedPrecision
                  ? applyNumberFormatting({
                      precision: userDefinedPrecision,
                      inputNumber: valueAsMoney.amt,
                      currencyCode: valueAsMoney.currency,
                      currencyDecimalDigits: valueAsMoney.dec,
                    })
                  : formatCurrency(valueAsMoney.amt, valueAsMoney.currency)}
              </>
            ) : (
              <Content id={ContentId.NA} />
            )}
          </TableCellDiv>
        );
      }

      let isPercentInner = isPercent;
      const isPercentOption =
        userDefinedPrecision &&
        Object.values(FormatOption_Percent).includes(
          userDefinedPrecision as FormatOption_Percent
        );
      if (isCustomColumn && isPercentOption) {
        isPercentInner = true;
      }

      if (isNumeric) {
        return (
          <TableCellDiv align={align}>
            {value != null
              ? applyNumberFormatting({
                  inputNumber: value,
                  isPercent: isPercentInner,
                  precision: isInteger
                    ? (userDefinedPrecision ??
                      FormatOption.Percent_RoundToWhole)
                    : userDefinedPrecision,
                })
              : (value ?? <Content id={ContentId.NA} />)}
          </TableCellDiv>
        );
      }

      return <TableCellDiv align={align}>{value}</TableCellDiv>;
    },
    sortingFn: isCurrency
      ? sortMoney<ReportMetricsResponse>
      : isBoolean
        ? sortNullableBoolean
        : sortMixedSingleAndArray,
  });
};
